// src/services/adapters/phonicsDataAdapter.ts
// Bridge layer: Mood-based config → Existing worksheet system
import worksheetWordsData from '../../data/worksheet_words.json'

// Types matching the brief's simpler structure
export type PhonicsPattern = 'cvc' | 'magic_e' | 'blends' | 'digraphs' | 'vowel_teams'
export type MoodType = 'overwhelmed' | 'high_energy' | 'steady'

export interface SkipOptions {
  noWriting: boolean
  noRhyming: boolean
  visualOnly: boolean
}

export interface MoodBasedConfig {
  pattern: PhonicsPattern
  mood: MoodType
  skipOptions: SkipOptions
}

export interface WordSelection {
  words: string[]
  wordCount: number
  difficulty: string[]
}

export interface ActivitySelection {
  activityTypes: string[]
  activityCount: number
}

export interface StylingConfig {
  colorScheme: string[]
  fontSize: string
  spacing: string
  encouragingMessages: string[]
  movementBreaks: string[]
}

// Utility: Shuffle array
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
  }
  return shuffled
}

/**
 * Select words from worksheet_words.json based on pattern and mood
 */
export function selectWordsForMood(
    pattern: PhonicsPattern,
    mood: MoodType
  ): WordSelection {
    // Get pattern data from JSON
    const patternData = (worksheetWordsData as any).patterns[pattern]
    if (!patternData) {
      throw new Error(`Pattern ${pattern} not found in worksheet_words.json`)
    }
  
    // Get mood configuration
    const moodConfig = (worksheetWordsData as any).mood_configurations[mood]
    if (!moodConfig) {
      throw new Error(`Mood ${mood} not found in worksheet_words.json`)
    }
  
    // Build word pool based on mood's difficulty filter
    let wordPool: string[] = []
    
    moodConfig.difficulty_filter.forEach((difficulty: string) => {
      const wordsAtDifficulty = patternData[difficulty] || []
      wordPool = [...wordPool, ...wordsAtDifficulty]
    })
  
    if (wordPool.length === 0) {
      throw new Error(`No words found for pattern ${pattern} with mood ${mood}`)
    }
  
    // Special handling for overwhelmed mode: NO shuffle, take first words (most familiar)
    let selectedWords: string[]
    if (mood === 'overwhelmed') {
      selectedWords = wordPool.slice(0, moodConfig.word_count)
      console.log(`📚 Overwhelmed mode: Using first ${moodConfig.word_count} most familiar words (no shuffle)`)
    } else {
      // For other moods: shuffle for variety
      const shuffled = shuffleArray(wordPool)
      selectedWords = shuffled.slice(0, moodConfig.word_count)
      console.log(`📚 ${mood} mode: Shuffled and selected ${moodConfig.word_count} words`)
    }
  
    console.log(`📝 Selected words:`, selectedWords)
  
    return {
      words: selectedWords,
      wordCount: selectedWords.length,
      difficulty: moodConfig.difficulty_filter
    }
}

/**
 * Select activities based on mood and skip options
 */
export function selectActivitiesForMood(
    mood: MoodType,
    skipOptions: SkipOptions
  ): ActivitySelection {
    // Get mood configuration for activity count and allowed types
    const moodConfig = (worksheetWordsData as any).mood_configurations[mood]
    const activityTemplates = (worksheetWordsData as any).activity_templates
  
    // Filter to activities compatible with this mood
    let compatibleActivities = [...moodConfig.activity_types]
    const filteredOut: string[] = []
  
    // Apply skip filters with better logging
    if (skipOptions.noWriting) {
      const beforeCount = compatibleActivities.length
      compatibleActivities = compatibleActivities.filter((type: string) => {
        const template = activityTemplates[type]
        const shouldKeep = template?.can_skip_if !== 'no_writing_today'
        if (!shouldKeep) filteredOut.push(`${type} (writing activity)`)
        return shouldKeep
      })
      console.log(`✂️ No Writing filter: ${beforeCount - compatibleActivities.length} activities removed`)
    }
  
    if (skipOptions.noRhyming) {
      const beforeCount = compatibleActivities.length
      compatibleActivities = compatibleActivities.filter((type: string) => {
        const template = activityTemplates[type]
        const shouldKeep = template?.skip_if !== 'no_rhyming_today'
        if (!shouldKeep) filteredOut.push(`${type} (rhyming activity)`)
        return shouldKeep
      })
      console.log(`✂️ No Rhyming filter: ${beforeCount - compatibleActivities.length} activities removed`)
    }
  
    if (skipOptions.visualOnly) {
      const beforeCount = compatibleActivities.length
      compatibleActivities = compatibleActivities.filter((type: string) => {
        const template = activityTemplates[type]
        const shouldKeep = template?.cognitive_load === 'low'
        if (!shouldKeep) filteredOut.push(`${type} (not low cognitive load)`)
        return shouldKeep
      })
      console.log(`✂️ Visual Only filter: ${beforeCount - compatibleActivities.length} activities removed`)
    }
  
    if (filteredOut.length > 0) {
      console.log('🚫 Filtered out activities:', filteredOut)
    }
  
    // Handle edge case: if no activities remain, fall back to safest activity
    if (compatibleActivities.length === 0) {
      console.error(
        `⚠️ All activities were filtered out! Filters applied: ` +
        `noWriting=${skipOptions.noWriting}, noRhyming=${skipOptions.noRhyming}, visualOnly=${skipOptions.visualOnly}. ` +
        `Falling back to 'circle_match' (safest activity).`
      )
      compatibleActivities = ['circle_match']
    }
  
    // Ensure we have enough activities after filtering
    if (compatibleActivities.length < moodConfig.activity_count) {
      console.warn(
        `⚠️ Only ${compatibleActivities.length} activities available after filtering, ` +
        `but ${moodConfig.activity_count} requested. Using all available.`
      )
    }
  
    // Select exactly ONE activity (activity_count should be 1 in JSON)
    const shuffled = shuffleArray(compatibleActivities)
    const selectedActivityTypes = shuffled.slice(0, 1) // Force single activity
  
    console.log(`✅ Selected activities for ${mood}:`, selectedActivityTypes)
  
    return {
      activityTypes: selectedActivityTypes,
      activityCount: selectedActivityTypes.length
    }
}

/**
 * Get styling configuration for the given mood
 */
export function getStylingForMood(mood: MoodType): StylingConfig {
    // Use pdf_styling (the actual key in worksheet_words.json)
    const stylingData = (worksheetWordsData as any).pdf_styling?.[mood]
    
    if (!stylingData) {
      throw new Error(`Styling not found for mood: ${mood}`)
    }
  
    return {
      colorScheme: stylingData.color_scheme,
      fontSize: stylingData.font_size,
      spacing: stylingData.spacing,
      encouragingMessages: stylingData.encouraging_messages,
      movementBreaks: stylingData.movement_breaks
    }
}

/**
 * Generate parent notes based on the configuration
 */
export function generateParentNotes(
  config: MoodBasedConfig,
  wordSelection: WordSelection,
  activitySelection: ActivitySelection
): {
  why: string
  how: string
  expectations: string
} {
  const moodConfig = (worksheetWordsData as any).mood_configurations[config.mood]
  const patternName = config.pattern.replace(/_/g, ' ')

  // Why these words
  const why = `These ${patternName} words were chosen because your child is in a "${config.mood.replace(/_/g, ' ')}" state today. We selected ${wordSelection.difficulty.join(' and ')} difficulty words to match their current capacity.`

  // How to support
  let how = moodConfig.parent_guidance || `Sit with your child and work through the activities at their pace.`
  
  if (config.skipOptions.noWriting) {
    how += ` Writing activities were skipped as requested—focus on recognition and verbal practice instead.`
  }
  if (config.skipOptions.visualOnly) {
    how += ` Only low cognitive load activities were included to reduce overwhelm.`
  }

  // Expectations
  const expectedTime = moodConfig.estimated_time_minutes || 15
  const expectations = `Expect this worksheet to take about ${expectedTime} minutes. Success means your child stayed regulated and engaged, not that they completed every item. Celebrate any participation.`

  return { why, how, expectations }
}

/**
 * Calculate estimated time based on activities and word count
 */
export function calculateEstimatedTime(
  activityCount: number,
  wordCount: number,
  mood: MoodType
): number {
  const moodConfig = (worksheetWordsData as any).mood_configurations[mood]
  return moodConfig.estimated_time_minutes || 15
}